import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemIcon,
  Divider,
  Autocomplete,
  Badge,
  Avatar,
  Paper,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import CodeIcon from "@mui/icons-material/Code";
import MarketingIcon from "@mui/icons-material/TrendingUp";
import FunnelIcon from "@mui/icons-material/FilterList";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimerIcon from "@mui/icons-material/Timer";
import Logo from "../../../Assets/images/logo.svg";
import Styled from "styled-components";
import MenuList from "./MenuList"; // Import the MenuList component
import { API_URL } from "../../../MasterData/index";

// Move the ImageWrap styled component outside the functional component
// Use props instead of directly accessing isDarkTheme
const ImageWrap = Styled.div`
  .logo_image {
    width: auto;
    height: 45px;
    margin: 8px auto;
    display: block;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    filter: ${props => props.isDarkTheme ? 'brightness(1.2)' : 'none'};
  }
  .logo_image:hover {
    transform: scale(1.05);
    filter: ${props => props.isDarkTheme ? 'brightness(1.5)' : 'brightness(1.1)'};
  }
`;

const ResponsiveAppBar = ({ isDarkTheme }) => {
  const [openDialog, setOpenDialog] = useState(false); // To control logout confirmation dialog
  const [drawerOpen, setDrawerOpen] = useState(false); // To control the side menu
  const [currentTime, setCurrentTime] = useState(new Date());
  const [remainingTime, setRemainingTime] = useState(10 * 60 * 60); // 10 hours in seconds
  const [logoutReason, setLogoutReason] = useState("Break"); // Default to "Break"
  const [error, setError] = useState(false); // To track if there's an error in the reason input
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Loading state for logout process

  // Check authentication status
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  
  // Retrieve login time from localStorage
  const loginTime = localStorage.getItem("loginTime");
  const startTime = loginTime ? new Date(parseInt(loginTime, 10)) : new Date(); // Parse loginTime to Date
  const endTime = new Date(startTime.getTime() + 10 * 60 * 60 * 1000); // Calculate end time (10 hours from start time)

  const navigate = useNavigate();
  const location = useLocation();

  // User information - properly defined
  const userId = localStorage.getItem("userId");
  const roleName = localStorage.getItem("roleName");
  const userName = localStorage.getItem("userName");
  const staffId = localStorage.getItem("staffId");
  const roleId = localStorage.getItem("roleId"); 
  const depId = localStorage.getItem("depId"); 
  const gradeCode = localStorage.getItem("gradeCode"); 
  
  const depName = localStorage.getItem("depName");

  const handleLogoClick = () => {
    const isLoggedIn = localStorage.getItem("isAuthenticated");
    if (isLoggedIn) {
      navigate("/task-dashboard");
    } else {
      navigate("/");
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen); // Toggle the drawer open/close state
  };

  const handleMenuItemClick = (path) => {
    navigate(path); // Navigate to the selected menu item
    setDrawerOpen(false); // Close the drawer after navigation
  };

  const handleLogoutClick = () => {
    setLogoutReason("Break"); // Set default reason to "Break" when logout button is clicked
    setOpenDialog(true); // Open the confirmation dialog for logout
  };

  const handleLogoutConfirm = async () => {
    if (!logoutReason.trim()) {
      setError(true); // Set error if the reason is empty
      return;
    }

    setIsLoggingOut(true); // Start loading state

    try {
      const userId = localStorage.getItem("userId"); // Retrieve user ID from localStorage
      await fetch(`${API_URL}/user-auth/Staff/LogoutReason`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          reason: logoutReason, // Send the reason for logout
        }),
      });
      
      // Add a small delay to make the loading state visible (better UX)
      setTimeout(() => {
        localStorage.clear();
        setOpenDialog(false);
        setIsLoggingOut(false);
        navigate("/"); // Redirect to login page
      }, 800);
    } catch (error) {
      console.error("Error while calling the API:", error);
      setIsLoggingOut(false); // Reset loading state in case of error
    }
  };

  const handleLogoutCancel = () => {
    setOpenDialog(false); // Close the confirmation dialog without logging out
    setLogoutReason(""); // Clear the reason input
    setError(false); // Clear the error state
  };

  const isLoginPage = location.pathname === "/";

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Update remaining time every second
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      const now = new Date(); // Get the current time
      const timeLeft = Math.max(0, Math.floor((endTime - now) / 1000)); // Calculate remaining time in seconds
      setRemainingTime(timeLeft);

      // If the countdown reaches zero, clear the interval
      if (timeLeft <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    return () => clearInterval(countdownInterval); // Cleanup interval on component unmount
  }, [endTime]);

  // Format date in DD-MMM-YYYY format
  const formatDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format time in HH:MM:SS AM/PM format
  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  // Format remaining time in HH:MM:SS
  const formatRemainingTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Calculate percentage of time elapsed
  const calculateTimeProgress = () => {
    const totalDuration = 10 * 60 * 60; // 10 hours in seconds
    const elapsed = totalDuration - remainingTime;
    return Math.min(100, (elapsed / totalDuration) * 100);
  };

  // Get color based on remaining time
  const getTimeColor = () => {
    const percentage = calculateTimeProgress();
    if (percentage < 50) return isDarkTheme ? "#4caf50" : "#388e3c"; // Green
    if (percentage < 75) return isDarkTheme ? "#ff9800" : "#f57c00"; // Orange
    return isDarkTheme ? "#f44336" : "#d32f2f"; // Red
  };

  return (
    <Box sx={{ position: "relative" }}>
      <AppBar
        className="customize_header"
        position="static"
        sx={{
          background: isDarkTheme
            ? "linear-gradient(to right, #1a237e, #000000)"
            : "linear-gradient(to right, #ffffff, #f5f7fa)",
          color: isDarkTheme ? "#fff" : "#000",
          fontFamily: "DM Sans",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          borderRadius: "0 0 15px 15px",
        }}
      >
        <Container>
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              justifyContent: "space-between", // Space out elements
              alignItems: "center",
              flexWrap: "nowrap", // Prevent wrapping
              py: 0.5, // Add some vertical padding
            }}
          >
            {/* Left Section: Burger Menu and Logo */}
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {/* Conditionally Render Burger Menu */}
              {!isLoginPage && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    marginRight: "16px",
                    transition: "all 0.3s ease-in-out",
                    background: isDarkTheme 
                      ? "rgba(255,255,255,0.1)" 
                      : "rgba(0,0,0,0.04)",
                    "&:hover": {
                      transform: "scale(1.05)",
                      background: isDarkTheme 
                        ? "rgba(255,255,255,0.2)" 
                        : "rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {/* Logo */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginRight: "24px",
                  transition: "all 0.3s ease",
                  flexDirection: "column", // Stack logo and text vertically
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={handleLogoClick}
              >                <ImageWrap isDarkTheme={isDarkTheme}>
                  <img
                    className="logo_image"
                    src={Logo}
                    alt="Enquiry"
                  />
                </ImageWrap>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: isDarkTheme ? "#fff" : "#000",
                    letterSpacing: "0.5px",
                    mt: -0.5, // Negative margin to bring it closer to the logo
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  StaffSync360
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "10px",
                    color: isDarkTheme ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    letterSpacing: "0.3px",
                    mt: -0.8, // Negative margin to bring it closer to the text above
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  AUJ Group
                </Typography>
              </Box>
            </Box>

            {/* Center Current Time and Countdown Timer */}
            {!isLoginPage && ( // Only display this section if not on the login page
              <Paper 
                elevation={isDarkTheme ? 4 : 2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexGrow: 1, // Center-align this section
                  justifyContent: "center",
                  gap: "8px", // Add spacing between rows
                  background: isDarkTheme 
                    ? "rgba(255,255,255,0.08)" 
                    : "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(10px)",
                  padding: "12px 20px",
                  borderRadius: "12px",
                  boxShadow: isDarkTheme 
                    ? "inset 0 0 5px rgba(255,255,255,0.1)" 
                    : "inset 0 0 10px rgba(0,0,0,0.03)",
                  mx: 2,
                  transform: "translateY(0px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: isDarkTheme 
                      ? "0 8px 20px rgba(0,0,0,0.3), inset 0 0 5px rgba(255,255,255,0.2)" 
                      : "0 8px 15px rgba(0,0,0,0.1), inset 0 0 10px rgba(0,0,0,0.03)",
                  }
                }}
              >
                {/* First Row: Date, Start Time, End Time */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "24px", // Add spacing between items
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: isDarkTheme ? "#90caf9" : "#3949ab" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600",
                        color: isDarkTheme ? "#fff" : "#000",
                      }}
                    >
                      {formatDate(currentTime)} 
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: isDarkTheme ? "#4caf50" : "#388e3c" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600",
                        color: isDarkTheme ? "#4caf50" : "#388e3c",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: "500", color: isDarkTheme ? "#a5d6a7" : "#2e7d32" }}>
                        Started:
                      </Box>{" "}
                      {formatTime(startTime)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TimerIcon fontSize="small" sx={{ mr: 1, color: isDarkTheme ? "#f44336" : "#d32f2f" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600",
                        color: isDarkTheme ? "#f44336" : "#d32f2f",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: "500", color: isDarkTheme ? "#ef9a9a" : "#c62828" }}>
                        Ends:
                      </Box>{" "}
                      {formatTime(endTime)}
                    </Typography>
                  </Box>
                </Box>

                {/* Second Row: Current Time, Remaining Time */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "24px", // Add spacing between items
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={`Current: ${formatTime(currentTime)}`}
                    variant="outlined"
                    sx={{
                      fontWeight: "bold",
                      borderColor: isDarkTheme ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                      color: isDarkTheme ? "#fff" : "#000",
                      "& .MuiChip-icon": {
                        color: isDarkTheme ? "#90caf9" : "#3949ab",
                      },
                    }}
                  />
                  
                  <Chip
                    icon={<TimerIcon />}
                    label={`Remaining: ${formatRemainingTime(remainingTime)}`}
                    color={remainingTime < 3600 ? "error" : "default"}
                    variant="outlined"
                    sx={{
                      fontWeight: "bold",
                      borderColor: getTimeColor(),
                      color: getTimeColor(),
                      "& .MuiChip-icon": {
                        color: getTimeColor(),
                      },
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* Conditionally render Logout Button */}
            {!isLoginPage && (
              <Box sx={{ flexGrow: 0 }}>
                <Button
                  variant="contained"
                  onClick={handleLogoutClick}
                  startIcon={<LogoutIcon />}
                  sx={{
                    marginLeft: "auto",
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    padding: "8px 20px",
                    background: isDarkTheme
                      ? "linear-gradient(45deg, #f44336, #d32f2f)"
                      : "linear-gradient(45deg, #ef5350, #e53935)",
                    boxShadow: "0 4px 10px rgba(244, 67, 54, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: isDarkTheme
                        ? "linear-gradient(45deg, #d32f2f, #b71c1c)"
                        : "linear-gradient(45deg, #e53935, #c62828)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 15px rgba(244, 67, 54, 0.4)",
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}

            {/* Conditionally render Login Button */}
            {isLoginPage && (
              <Box sx={{ flexGrow: 0 }}>
                <Button
                  variant="contained"
                  name="staff-login"
                  id="staff-login"

                  onClick={() => {
                    // Directly trigger the login overlay without navigation
                    if (window.showLoginOverlay) {
                      window.showLoginOverlay();
                    }
                  }}
                  startIcon={<LoginIcon />}
                  sx={{
                    marginLeft: "auto",
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    padding: "8px 20px",
                    background: isDarkTheme
                      ? "linear-gradient(45deg, #4caf50, #388e3c)"
                      : "linear-gradient(45deg, #66bb6a, #43a047)",
                    boxShadow: "0 4px 10px rgba(76, 175, 80, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: isDarkTheme
                        ? "linear-gradient(45deg, #388e3c, #2e7d32)"
                        : "linear-gradient(45deg, #43a047, #388e3c)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 15px rgba(76, 175, 80, 0.4)",
                    },
                  }}
                >
                  Staff Login
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Side Drawer Menu */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 280, // Increased width of the drawer
            background: isDarkTheme 
              ? "linear-gradient(to bottom, #1a237e, #121212)" 
              : "linear-gradient(to bottom, #ffffff, #f5f7fa)",
            color: isDarkTheme ? "#fff" : "#000",
            borderRadius: "0 15px 15px 0",
            padding: "0",
            boxShadow: "5px 0 20px rgba(0, 0, 0, 0.2)",
            overflowY: "auto", // Ensure drawer content is scrollable
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%", // Make sure drawer doesn't exceed viewport height
          },
        }}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            top: 0, // Ensure drawer starts from the top
            height: '100%', // Full height
          },
          zIndex: 1300, // Ensure drawer appears above other elements
        }}
      >
        {/* User Profile Section - More refined and professional */}
        <Box sx={{ 
          padding: '12px 16px', 
          position: 'sticky',
          top: 0,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isDarkTheme 
            ? "rgba(26, 35, 126, 0.95)" 
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          zIndex: 2,
        }}>
          <Avatar 
            sx={{ 
              width: 46, 
              height: 46, 
              bgcolor: isDarkTheme ? "#3949ab" : "#3f51b5",
              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
              border: isDarkTheme ? "2px solid #90caf9" : "2px solid #fff",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            {userName?.charAt(0)?.toUpperCase() || "M"}
          </Avatar>
          
          <Box sx={{ ml: 1.5, flexGrow: 1, overflow: 'hidden' }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: "600",
                lineHeight: 1.2,
                color: isDarkTheme ? "#fff" : "#000",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {userName}
            </Typography>
            
            <Box 
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mt: 0.5
              }}
            >
              <Box 
                sx={{
                  display: "inline-flex",
                  backgroundColor: isDarkTheme ? "rgba(25, 118, 210, 0.2)" : "rgba(25, 118, 210, 0.1)",
                  color: isDarkTheme ? "#90caf9" : "#1976d2",
                  fontWeight: "medium",
                  fontSize: "10px",
                  padding: "1px 6px",
                  borderRadius: "10px",
                  alignItems: "center",
                  height: "18px",
                  whiteSpace: 'nowrap'
                }}
              >
                ID: {userId}
              </Box>
              {/* <Typography 
                sx={{ 
                  fontSize: "10px", 
                  color: isDarkTheme ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {roleName}
              </Typography> */}
            </Box>
          </Box>
        </Box>

        {/* Divider Below the User Profile */}
        <Divider 
          sx={{ 
            backgroundColor: isDarkTheme ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
          }} 
        />

        {/* User Information Card - More refined version */}
        <Box sx={{ p: 1.5, pt: 1.25 }}>
          <Paper
            elevation={isDarkTheme ? 1 : 0}
            sx={{
              backgroundColor: isDarkTheme ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
              borderRadius: "10px",
              overflow: "hidden",
              border: isDarkTheme ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.05)",
              transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkTheme 
                  ? "0 5px 10px rgba(0,0,0,0.4)" 
                  : "0 5px 15px rgba(0,0,0,0.1)"
              }
            }}
          >
           
            {/* Compact user info grid with improved styling */}
            <Box sx={{ p: 1.25 }}>
              {/* Staff ID */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 0.9,
                pb: 0.9,
                borderBottom: isDarkTheme 
                  ? "1px dashed rgba(255,255,255,0.08)" 
                  : "1px dashed rgba(0,0,0,0.06)"
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: isDarkTheme ? "#90caf9" : "#3949ab",
                      mr: 1 
                    }} 
                  />
                  <Typography sx={{ 
                    fontSize: '10px', 
                    color: isDarkTheme ? '#bbb' : '#666',
                    fontWeight: '500'
                  }}>
                    Staff ID
                  </Typography>
                </Box>
                <Chip 
                  label={staffId} 
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '10px',
                    backgroundColor: isDarkTheme ? "rgba(57, 73, 171, 0.15)" : "rgba(57, 73, 171, 0.08)",
                    color: isDarkTheme ? "#90caf9" : "#3949ab",
                    fontWeight: '500',
                    '& .MuiChip-label': { px: 1 }
                  }} 
                />
              </Box>
              
              {/* Dep ID */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 0.9,
                pb: 0.9,
                borderBottom: isDarkTheme 
                  ? "1px dashed rgba(255,255,255,0.08)" 
                  : "1px dashed rgba(0,0,0,0.06)"
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: isDarkTheme ? "#ce93d8" : "#7b1fa2",
                      mr: 1 
                    }} 
                  />
                  <Typography sx={{ 
                    fontSize: '10px', 
                    color: isDarkTheme ? '#bbb' : '#666',
                    fontWeight: '500'
                  }}>
                    Department ID
                  </Typography>
                </Box>
                <Chip 
                  label={depId} 
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '10px',
                    backgroundColor: isDarkTheme ? "rgba(156, 39, 176, 0.15)" : "rgba(156, 39, 176, 0.08)",
                    color: isDarkTheme ? "#ce93d8" : "#7b1fa2",
                    fontWeight: '500',
                    '& .MuiChip-label': { px: 1 }
                  }} 
                />
              </Box>
              
              {/* Role ID */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 0.9,
                pb: 0.9,
                borderBottom: isDarkTheme 
                  ? "1px dashed rgba(255,255,255,0.08)" 
                  : "1px dashed rgba(0,0,0,0.06)"
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: isDarkTheme ? "#80cbc4" : "#00796b",
                      mr: 1 
                    }} 
                  />
                  <Typography sx={{ 
                    fontSize: '10px', 
                    color: isDarkTheme ? '#bbb' : '#666',
                    fontWeight: '500'
                  }}>
                    Role ID
                  </Typography>
                </Box>
                <Chip 
                  label={roleId} 
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '10px',
                    backgroundColor: isDarkTheme ? "rgba(0, 150, 136, 0.15)" : "rgba(0, 150, 136, 0.08)",
                    color: isDarkTheme ? "#80cbc4" : "#00796b",
                    fontWeight: '500',
                    '& .MuiChip-label': { px: 1 }
                  }} 
                />
              </Box>
              
              {/* Role Name */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 0.9,
                pb: 0.9,
                borderBottom: isDarkTheme 
                  ? "1px dashed rgba(255,255,255,0.08)" 
                  : "1px dashed rgba(0,0,0,0.06)"
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: isDarkTheme ? "#ffb74d" : "#f57c00",
                      mr: 1 
                    }} 
                  />
                  <Typography sx={{ 
                    fontSize: '10px', 
                    color: isDarkTheme ? '#bbb' : '#666',
                    fontWeight: '500'
                  }}>
                    Role Name
                  </Typography>
                </Box>
                <Typography sx={{ 
                  fontSize: '10px', 
                  color: isDarkTheme ? '#fff' : '#000',
                  maxWidth: '135px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  backgroundColor: isDarkTheme ? "rgba(255, 152, 0, 0.1)" : "rgba(255, 152, 0, 0.05)",
                  py: 0.25,
                  px: 1,
                  borderRadius: 1
                }}>
                  {roleName}
                </Typography>
              </Box>
              
              {/* Dep Name */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: isDarkTheme ? "#9fa8da" : "#3949ab",
                      mr: 1 
                    }} 
                  />
                  <Typography sx={{ 
                    fontSize: '10px', 
                    color: isDarkTheme ? '#bbb' : '#666',
                    fontWeight: '500'
                  }}>
                    Department
                  </Typography>
                </Box>
                <Typography sx={{ 
                  fontSize: '10px', 
                  color: isDarkTheme ? '#fff' : '#000',
                  maxWidth: '135px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  backgroundColor: isDarkTheme ? "rgba(63, 81, 181, 0.1)" : "rgba(63, 81, 181, 0.05)",
                  py: 0.25,
                  px: 1,
                  borderRadius: 1
                }}>
                  {depName}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Divider Before Menu */}
        <Divider 
          sx={{ 
            backgroundColor: isDarkTheme ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
            my: 0.75
          }} 
        />

        {/* Menu Items - Scrollable content */}
        <Box sx={{
          flexGrow: 1,
          overflowY: "auto", // Allow scrolling of menu items
          pb: 2, // Add some padding at the bottom
        }}>
          <MenuList
            roleId={roleId}
            gradeCode={gradeCode}
            isDarkTheme={isDarkTheme}
            handleMenuItemClick={handleMenuItemClick}
          />
        </Box>
      </Drawer>

      {/* Confirmation Dialog for Logout */}
      <Dialog
        open={openDialog}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            padding: "24px",
            borderRadius: "16px",
            backgroundColor: isDarkTheme ? "#333" : "#fff",
            color: isDarkTheme ? "#fff" : "#000",
            boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.3)",
            maxWidth: "450px",
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontWeight: "bold",
            fontSize: "22px",
            color: isDarkTheme ? "#ff5722" : "#d32f2f",
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: isDarkTheme 
                ? "rgba(244, 67, 54, 0.2)"
                : "rgba(244, 67, 54, 0.1)",
            }}
          >
            <LogoutIcon sx={{ fontSize: "28px" }} />
          </Box>
          Confirm Logout
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              marginBottom: "20px",
              fontSize: "16px",
              color: isDarkTheme ? "#ddd" : "#333",
              lineHeight: 1.6,
            }}
          >
            Please provide a reason for logging out. This helps us improve your experience.
          </Typography>
          <Autocomplete
            freeSolo
            options={["Break", "Meeting", "Lunch", "Finish My Shift", "Medical", "Day End", "Personal Reasons"]}
            value={logoutReason}
            onChange={(event, newValue) => {
              setLogoutReason(newValue || ""); // Update the reason when a suggestion is selected
              setError(false); // Clear the error when a valid reason is selected
            }}
            inputValue={logoutReason}
            onInputChange={(event, newInputValue) => {
              setLogoutReason(newInputValue); // Update the reason as the user types
              setError(false); // Clear the error when the user starts typing
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Enter your reason here..."
                error={error} // Highlight the text box in red if there's an error
                helperText={error ? "Reason is required." : ""} // Display error message below the text box
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": {
                      borderColor: error ? "#d32f2f" : isDarkTheme ? "#555" : "#ccc",
                      borderWidth: "1.5px",
                    },
                    "&:hover fieldset": {
                      borderColor: error ? "#d32f2f" : isDarkTheme ? "#888" : "#888",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: error ? "#d32f2f" : isDarkTheme ? "#ff5722" : "#1976d2",
                    },
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            padding: "16px 24px",
            pt: 3,
          }}
        >
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "30px",
              padding: "8px 20px",
              borderColor: isDarkTheme ? "#888" : "#ccc",
              color: isDarkTheme ? "#fff" : "#000",
              "&:hover": {
                borderColor: isDarkTheme ? "#fff" : "#888",
                backgroundColor: isDarkTheme ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            disabled={isLoggingOut}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              padding: isLoggingOut ? "8px 16px" : "8px 24px",
              borderRadius: "30px",
              backgroundColor: isDarkTheme ? "#ff5722" : "#d32f2f",
              boxShadow: "0 4px 10px rgba(244, 67, 54, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              "&:hover": {
                backgroundColor: isDarkTheme ? "#ff7043" : "#e57373",
                boxShadow: "0 6px 15px rgba(244, 67, 54, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {isLoggingOut && (
              <Box
                sx={{
                  display: "inline-block",
                  width: "16px",
                  height: "16px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "2px solid #fff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            )}
            {isLoggingOut ? "Logging out..." : "Logout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponsiveAppBar;